import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ClipboardList, ArrowLeft, Send, FileText, Check, Mail, MessageCircle, Download, FileDown, RefreshCw, StickyNote, Pencil, Trash2, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getAdminOrders, getAdminOrder, updateOrderStatus, updateOrderNotes, sendProforma, getExportProviderUrl, getProformaPdfUrl, resendStatusEmail, editOrder, cancelOrder, deleteOrder } from '../../lib/api';
import { Toaster, toast } from 'sonner';

const ConfirmModal = ({ open, title, message, warning, onConfirm, onCancel, confirmLabel = 'Confirm', danger }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="confirm-modal">
        <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
        <p className="text-white/60 text-sm mb-4">{message}</p>
        {warning && <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs mb-4"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{warning}</span></div>}
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-white/60 hover:text-white" data-testid="confirm-cancel-btn">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 text-sm font-medium ${danger ? 'bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30' : 'bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/30'}`} data-testid="confirm-action-btn">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

const ORDER_STATUS_KEYS = [
  { value: 'pending_approval', color: '#F59E0B' },
  { value: 'proforma_sent', color: '#00D4FF' },
  { value: 'awaiting_wire', color: '#F59E0B' },
  { value: 'paid', color: '#10B981' },
  { value: 'processing', color: '#00D4FF' },
  { value: 'shipped', color: '#10B981' },
  { value: 'completed', color: '#10B981' },
  { value: 'cancelled', color: '#EF4444' },
];

export const AdminOrders = () => {
  const { t } = useApp();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await getAdminOrders({ status: statusFilter || undefined });
      setOrders(res.data);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      toast.error(t('adminOrders.failedLoadOrders'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const getStatusInfo = (status) => {
    return ORDER_STATUS_KEYS.find(s => s.value === status) || ORDER_STATUS_KEYS[0];
  };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-orders">
      <Toaster position="top-right" theme="dark" />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <ClipboardList className="w-8 h-8 text-[#00D4FF]" />
            {t('adminOrders.title')}
          </h1>
          <p className="text-white/60 mt-1">{orders.length} {t('adminOrders.title').toLowerCase()}</p>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] min-w-[180px]"
          data-testid="order-status-filter"
        >
          <option value="">{t('adminOrders.allStatuses')}</option>
          {ORDER_STATUS_KEYS.map((s) => (
            <option key={s.value} value={s.value}>{t(`status.${s.value}`)}</option>
          ))}
        </select>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="card-dark p-8 text-center">
          <div className="animate-pulse text-white/40">{t('common.loading')}</div>
        </div>
      ) : orders.length === 0 ? (
        <div className="card-dark p-8 text-center">
          <p className="text-white/40">{t('adminOrders.noOrders')}</p>
        </div>
      ) : (
        <div className="card-dark overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-terminal">
              <thead>
                <tr>
                  <th className="text-left">{t('adminOrders.orderNumber')}</th>
                  <th className="text-left">{t('adminOrders.company')}</th>
                  <th className="text-left">{t('adminOrders.contact')}</th>
                  <th className="text-left">{t('adminOrders.location')}</th>
                  <th className="text-right">{t('adminOrders.units')}</th>
                  <th className="text-right">{t('adminOrders.total')}</th>
                  <th className="text-center">{t('adminOrders.updateStatus').split(' ').pop()}</th>
                  <th className="text-left">{t('adminOrders.date')}</th>
                  <th className="text-right">{t('adminOrders.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const statusInfo = getStatusInfo(order.status);
                  return (
                    <tr key={order.id} data-testid={`order-row-${order.id}`}>
                      <td className="font-mono text-[#00D4FF]">{order.order_number}</td>
                      <td>{order.company_name}</td>
                      <td className="text-white/60">{order.contact_name}</td>
                      <td className="text-white/60">{order.city}, {order.country}</td>
                      <td className="text-right font-mono">{order.items.length}</td>
                      <td className="text-right font-mono text-[#00D4FF]">
                        {order.total_amount ? `$${order.total_amount.toLocaleString()}` : <span className="text-[#F59E0B] text-xs">Pending Quote</span>}
                      </td>
                      <td className="text-center">
                        <span 
                          className="status-badge"
                          style={{ backgroundColor: `${statusInfo.color}20`, color: statusInfo.color }}
                        >
                          {t(`status.${order.status}`)}
                        </span>
                      </td>
                      <td className="text-white/40 text-sm">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="text-right">
                        <Link
                          to={`/admin/orders/${order.id}`}
                          className="text-[#00D4FF] hover:underline text-sm"
                        >
                          {t('adminOrders.view')}
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export const AdminOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useApp();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [sendingProforma, setSendingProforma] = useState(false);
  const [resending, setResending] = useState(false);
  const [supplierNotes, setSupplierNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const fetchOrder = async () => {
    try {
      const res = await getAdminOrder(id);
      setOrder(res.data);
      setSupplierNotes(res.data.supplier_confirmation_notes || '');
    } catch (err) {
      console.error('Failed to fetch order:', err);
      toast.error(t('adminOrders.failedLoadOrder'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      const res = await updateOrderStatus(id, { status: newStatus });
      const statusLabel = t(`status.${newStatus}`);
      if (res.data.email_sent) {
        toast.success(`${t('adminOrders.statusUpdatedMsg')}: ${statusLabel} — ${t('adminOrders.statusUpdatedNotified')}`);
      } else if (res.data.email_failed) {
        toast.warning(t('adminOrders.statusUpdatedNoEmail'));
      } else {
        toast.success(`${t('adminOrders.statusUpdatedMsg')}: ${statusLabel}`);
      }
      fetchOrder();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || t('adminOrders.failedUpdateStatus'));
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await updateOrderNotes(id, supplierNotes);
      toast.success(t('adminOrders.notesSaved'));
      fetchOrder();
    } catch (err) {
      toast.error(t('adminOrders.failedSaveNotes'));
    } finally {
      setSavingNotes(false);
    }
  };

  const handleResendEmail = async () => {
    setResending(true);
    try {
      const res = await resendStatusEmail(id);
      if (res.data.sent) {
        toast.success(t('adminOrders.emailResentSuccess'));
      } else {
        toast.error(res.data.error || t('adminOrders.failedResendEmail'));
      }
    } catch (err) {
      toast.error(t('adminOrders.failedResendEmail'));
    } finally {
      setResending(false);
    }
  };

  const doAction = async (fn, successMsg) => {
    try {
      setActionLoading(true);
      await fn();
      if (successMsg) toast.success(successMsg);
      await fetchOrder();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
      setConfirm(null);
    }
  };

  const handleSendProforma = () => {
    setConfirm({
      type: 'proforma',
      title: t('adminOrders.sendProforma'),
      message: `Send proforma invoice for order ${order?.order_number} to ${order?.email}?`,
      confirmLabel: t('adminOrders.sendProforma'),
    });
  };

  const handleSaveEdit = async () => {
    await doAction(() => editOrder(id, editForm), 'Order updated');
    setEditing(false);
  };

  const handleExportProvider = () => {
    const token = localStorage.getItem('luxortek_admin_token');
    const url = getExportProviderUrl(id);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) throw new Error('Export failed');
        const disposition = res.headers.get('content-disposition') || '';
        const filename = disposition.split('filename=')[1]?.replace(/"/g, '') || `LUXORTEK_ProviderPick_${order?.order_number}.xlsx`;
        return res.blob().then(blob => ({ blob, filename }));
      })
      .then(({ blob, filename }) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success(t('adminOrders.pickListDownloaded'));
      })
      .catch(() => toast.error(t('adminOrders.failedExportPickList')));
  };

  const getStatusInfo = (status) => {
    return ORDER_STATUS_KEYS.find(s => s.value === status) || ORDER_STATUS_KEYS[0];
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="card-dark p-8 text-center">
          <div className="animate-pulse text-white/40">{t('adminOrders.loadingOrder')}</div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6 lg:p-8">
        <div className="card-dark p-8 text-center">
          <p className="text-white/40">{t('adminOrders.orderNotFound')}</p>
        </div>
      </div>
    );
  }

  const currentStatus = getStatusInfo(order.status);
  const currentStatusIndex = ORDER_STATUS_KEYS.findIndex(s => s.value === order.status);
  const s = order.status;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-order-detail">
      <Toaster position="top-right" theme="dark" />
      
      {/* Header */}
      <div className="mb-6">
        <Link to="/admin/orders" className="flex items-center gap-2 text-white/60 hover:text-white mb-4">
          <ArrowLeft className="w-4 h-4" />
          {t('adminOrders.backToOrders')}
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-mono text-[#00D4FF]">{order.order_number}</h1>
            <p className="text-white/60 mt-1">
              {t('adminOrders.date')}: {new Date(order.created_at).toLocaleString(language === 'es' ? 'es' : 'en')}
            </p>
            {order.source_quote_id && (
              <Link to={`/admin/quotes/${order.source_quote_id}`} className="inline-flex items-center gap-1.5 mt-1 text-sm text-purple-400 hover:text-purple-300 hover:underline" data-testid="source-quote-link">
                <FileText className="w-3.5 h-3.5" />
                From Quote: {order.source_quote_id}
              </Link>
            )}
          </div>
          <span 
            className="status-badge self-start"
            style={{ backgroundColor: `${currentStatus.color}20`, color: currentStatus.color }}
          >
            {t(`status.${order.status}`)}
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="order-actions">
        {/* Status progression buttons */}
        {s === 'pending_approval' && (
          <button onClick={handleSendProforma} disabled={actionLoading || sendingProforma} className="flex items-center gap-2 px-3 py-2 bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] text-sm font-medium disabled:opacity-50" data-testid="btn-send-proforma">
            <Send className="w-4 h-4" />Send Proforma
          </button>
        )}
        {s === 'paid' && (
          <button onClick={() => setConfirm({ type: 'processing', title: 'Mark Processing', message: `Mark order ${order.order_number} as processing?`, confirmLabel: 'Mark Processing' })} disabled={actionLoading || updating} className="flex items-center gap-2 px-3 py-2 bg-blue-500/20 border border-blue-500/40 text-blue-400 text-sm font-medium disabled:opacity-50" data-testid="btn-mark-processing">
            <ClipboardList className="w-4 h-4" />Mark Processing
          </button>
        )}
        {s === 'shipped' && (
          <button onClick={() => setConfirm({ type: 'completed', title: 'Mark Completed', message: `Mark order ${order.order_number} as completed?`, confirmLabel: 'Mark Completed' })} disabled={actionLoading || updating} className="flex items-center gap-2 px-3 py-2 bg-green-500/20 border border-green-500/40 text-green-400 text-sm font-medium disabled:opacity-50" data-testid="btn-mark-completed">
            <Check className="w-4 h-4" />Mark Completed
          </button>
        )}

        {/* Edit */}
        {!editing && s !== 'cancelled' && (
          <button onClick={() => { setEditing(true); setEditForm({ company_name: order.company_name, contact_name: order.contact_name, email: order.email, whatsapp: order.whatsapp || '', country: order.country, city: order.city, notes: order.notes || '' }); }} className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 text-white/70 text-sm hover:text-white" data-testid="btn-edit-order">
            <Pencil className="w-4 h-4" />Edit
          </button>
        )}

        {/* Cancel */}
        {!['cancelled', 'completed'].includes(s) && (
          <button onClick={() => setConfirm({ type: 'cancel', title: 'Cancel Order', message: `Cancel order ${order.order_number}? The order will be marked as cancelled but not deleted.`, confirmLabel: 'Cancel Order', danger: true })} className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20" data-testid="btn-cancel-order">
            <XCircle className="w-4 h-4" />Cancel
          </button>
        )}

        {/* Delete */}
        <button onClick={() => setConfirm({ type: 'delete', title: 'Delete Order', message: `Permanently delete order ${order.order_number}? This action cannot be undone.`, warning: order.source_quote_id ? `This order was created from quote ${order.source_quote_id}. The source quote will NOT be deleted.` : undefined, confirmLabel: 'Delete Forever', danger: true })} className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20" data-testid="btn-delete-order">
          <Trash2 className="w-4 h-4" />Delete
        </button>
      </div>

      {/* Edit Form */}
      {editing && (
        <div className="card-dark p-5 mb-6 space-y-3" data-testid="order-edit-form">
          <h3 className="font-semibold text-sm text-white/60 uppercase">Edit Order</h3>
          {['company_name', 'contact_name', 'email', 'whatsapp', 'country', 'city'].map(f => (
            <div key={f}>
              <label className="text-xs text-white/40 block mb-1">{f.replace(/_/g, ' ')}</label>
              <input value={editForm[f] || ''} onChange={e => setEditForm(p => ({ ...p, [f]: e.target.value }))} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" data-testid={`edit-field-${f}`} />
            </div>
          ))}
          <div>
            <label className="text-xs text-white/40 block mb-1">Notes</label>
            <textarea value={editForm.notes || ''} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" rows={3} data-testid="edit-field-notes" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleSaveEdit} disabled={actionLoading} className="px-4 py-2 bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] text-sm font-medium" data-testid="btn-save-edit">Save</button>
            <button onClick={() => setEditing(false)} className="px-4 py-2 text-white/60 text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Order Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Source Quote Card */}
          {order.source_quote_id && (
            <div className="card-dark p-5 border-purple-500/20" data-testid="source-quote-card">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-purple-400" />
                <div>
                  <p className="text-sm text-white/40">Created from Quote</p>
                  <Link to={`/admin/quotes/${order.source_quote_id}`} className="font-mono text-purple-400 hover:text-purple-300 hover:underline text-sm font-semibold" data-testid="source-quote-card-link">
                    {order.source_quote_id}
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Customer Info */}
          <div className="card-dark p-6">
            <h2 className="text-lg font-semibold mb-4">{t('adminOrders.customerInfo')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-white/50 text-sm">{t('adminOrders.company')}</label>
                <p className="font-semibold">{order.company_name}</p>
              </div>
              <div>
                <label className="text-white/50 text-sm">{t('adminOrders.contact')}</label>
                <p className="font-semibold">{order.contact_name}</p>
              </div>
              <div>
                <label className="text-white/50 text-sm">{t('adminOrders.email')}</label>
                <p className="font-semibold">{order.email}</p>
              </div>
              <div>
                <label className="text-white/50 text-sm">{t('adminOrders.whatsapp')}</label>
                <p className="font-semibold">{order.whatsapp || '-'}</p>
              </div>
              <div>
                <label className="text-white/50 text-sm">{t('adminOrders.country')}</label>
                <p className="font-semibold">{order.city}{order.city && order.country ? ', ' : ''}{order.country}</p>
              </div>
            </div>
            {order.notes && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <label className="text-white/50 text-sm">{t('adminOrders.orderNotes')}</label>
                <p className="text-white/80 mt-1">{order.notes}</p>
              </div>
            )}
          </div>

          {/* Order Items */}
          <div className="card-dark p-6">
            <h2 className="text-lg font-semibold mb-4">{t('adminOrders.items')} ({order.items.length} {t('common.units')})</h2>
            <div className="overflow-x-auto">
              <table className="w-full table-terminal">
                <thead>
                  <tr>
                    <th className="text-left">{t('cart.uniqueNum')}</th>
                    <th className="text-left">{t('cart.brand')}</th>
                    <th className="text-left">{t('cart.model')}</th>
                    <th className="text-right">{t('cart.price')}</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, idx) => (
                    <tr key={idx}>
                      <td className="font-mono text-[#00D4FF]">{item.unique_id}</td>
                      <td>{item.oem}</td>
                      <td className="text-white/80">{item.model}</td>
                      <td className="text-right font-mono">
                        {item.sale_price != null ? `$${item.sale_price.toLocaleString()}` : <span className="text-[#F59E0B] text-xs">Pending</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-white/10">
                    <td colSpan="3" className="text-right font-semibold">{t('adminOrders.total')} ({order.items.length} {t('common.units')})</td>
                    <td className="text-right font-mono text-xl text-[#00D4FF] font-bold">
                      {order.total_amount ? `$${order.total_amount.toLocaleString()}` : <span className="text-[#F59E0B]">Pending Quote</span>}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Proforma Status */}
          {order.proforma_details && (
            <div className="card-dark p-6 border-[#10B981]/30">
              <div className="flex items-center gap-3 text-[#10B981]">
                <FileText className="w-5 h-5" />
                <span className="font-semibold">{t('adminOrders.proformaSent')}</span>
              </div>
              <p className="text-white/60 mt-2 text-sm">{order.proforma_details}</p>
            </div>
          )}

          {/* Supplier Confirmation Notes (Admin-only) */}
          <div className="card-dark p-6" data-testid="supplier-notes-section">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-[#F59E0B]" />
              {t('adminOrders.supplierNotes')}
            </h2>
            <p className="text-white/40 text-xs mb-3">{t('adminOrders.supplierNotesHint')}</p>
            <textarea
              value={supplierNotes}
              onChange={(e) => setSupplierNotes(e.target.value)}
              placeholder={t('adminOrders.supplierNotesPlaceholder')}
              className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#F59E0B] text-white text-sm min-h-[100px] resize-y"
              data-testid="supplier-notes-textarea"
            />
            <button
              onClick={handleSaveNotes}
              disabled={savingNotes}
              className="mt-3 btn-secondary text-sm flex items-center gap-2"
              data-testid="save-supplier-notes-btn"
            >
              {savingNotes ? t('adminOrders.savingNotes') : t('adminOrders.saveNotes')}
            </button>
          </div>

          {/* Timeline / Audit Trail */}
          {order.status_history?.length > 0 && (
            <div className="card-dark p-5" data-testid="order-timeline">
              <h3 className="text-white/40 text-xs uppercase mb-4 flex items-center gap-2"><Clock className="w-4 h-4" />Timeline</h3>
              <div className="space-y-3">
                {[...order.status_history].reverse().map((h, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-[#00D4FF]/60 mt-1.5 shrink-0" />
                    <div>
                      <span className="text-white font-medium">{t(`status.${h.status}`) || h.status?.replace(/_/g, ' ')}</span>
                      <div className="text-white/30 text-xs mt-0.5">{new Date(h.changed_at).toLocaleString(language === 'es' ? 'es' : 'en')} · {h.changed_by}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions Sidebar */}
        <div className="space-y-6">
          {/* Export Provider Pick List */}
          <div className="card-dark p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Download className="w-5 h-5 text-[#10B981]" />
              {t('adminOrders.exportPickList')}
            </h2>
            <p className="text-white/60 text-sm mb-4">
              {t('adminOrders.exportPickListDesc')}
            </p>
            <button
              onClick={handleExportProvider}
              className="btn-secondary w-full flex items-center justify-center gap-2"
              data-testid="export-provider-btn"
            >
              <Download className="w-4 h-4" />
              {t('productsPage.exportToExcel')}
            </button>
          </div>

          {/* PDF Proforma Invoice */}
          <div className="card-dark p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileDown className="w-5 h-5 text-[#00D4FF]" />
              {t('adminOrders.proformaInvoice')}
            </h2>
            <p className="text-white/60 text-sm mb-4">
              {t('adminOrders.proformaInvoiceDesc')}
            </p>
            <button
              onClick={() => {
                const token = localStorage.getItem('luxortek_admin_token');
                const url = getProformaPdfUrl(id);
                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                  .then(res => {
                    if (!res.ok) throw new Error('Failed');
                    return res.blob();
                  })
                  .then(blob => {
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `LUXORTEK_Proforma_${order?.order_number || id}_${new Date().toISOString().slice(0,10)}.pdf`;
                    a.click();
                    URL.revokeObjectURL(a.href);
                    toast.success(t('adminOrders.proformaPdfDownloaded'));
                  })
                  .catch(() => toast.error(t('adminOrders.failedGeneratePdf')));
              }}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="download-proforma-btn"
            >
              <FileDown className="w-4 h-4" />
              {t('adminOrders.downloadPdf')}
            </button>
          </div>

          {/* Status Progress */}
          <div className="card-dark p-6">
            <h2 className="text-lg font-semibold mb-4">{t('orderDetail.orderProgress')}</h2>
            <div className="space-y-3">
              {ORDER_STATUS_KEYS.map((status, idx) => {
                const isPast = idx < currentStatusIndex;
                const isCurrent = idx === currentStatusIndex;
                const isFuture = idx > currentStatusIndex;
                
                return (
                  <div 
                    key={status.value}
                    className={`flex items-center gap-3 p-3 ${
                      isCurrent ? 'bg-white/5' : ''
                    }`}
                  >
                    <div 
                      className={`w-8 h-8 flex items-center justify-center ${
                        isPast || isCurrent ? '' : 'bg-white/5'
                      }`}
                      style={{ 
                        backgroundColor: (isPast || isCurrent) ? `${status.color}20` : undefined,
                        color: (isPast || isCurrent) ? status.color : 'rgba(255,255,255,0.3)'
                      }}
                    >
                      {isPast ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <span className="text-sm font-mono">{idx + 1}</span>
                      )}
                    </div>
                    <span className={`flex-1 ${isFuture ? 'text-white/30' : ''}`}>
                      {t(`status.${status.value}`)}
                    </span>
                    {isCurrent && !isFuture && idx < ORDER_STATUS_KEYS.length - 1 && idx > 0 && (
                      <button
                        onClick={() => handleStatusUpdate(ORDER_STATUS_KEYS[idx + 1].value)}
                        disabled={updating}
                        className="text-[#00D4FF] text-xs hover:underline"
                      >
                        {t('adminOrders.nextStep')} →
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card-dark p-6">
            <h2 className="text-lg font-semibold mb-4">{t('adminOrders.contactCustomer')}</h2>
            <div className="space-y-3">
              <a
                href={`mailto:${order.email}?subject=Re: Order ${order.order_number}`}
                className="btn-secondary w-full flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" />
                {t('adminOrders.emailCustomer')}
              </a>
              {order.whatsapp && (
                <a
                  href={`https://wa.me/${order.whatsapp.replace(/[^0-9]/g, '')}?text=Re: Order ${order.order_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary w-full flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              )}
            </div>
          </div>

          {/* Manual Status Update */}
          <div className="card-dark p-6">
            <h2 className="text-lg font-semibold mb-4">{t('adminOrders.updateStatus')}</h2>
            <select
              value={order.status}
              onChange={(e) => handleStatusUpdate(e.target.value)}
              disabled={updating}
              className="w-full px-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
              data-testid="status-select"
            >
              {ORDER_STATUS_KEYS.map((s) => (
                <option key={s.value} value={s.value}>{t(`status.${s.value}`)}</option>
              ))}
            </select>
            <button
              onClick={handleResendEmail}
              disabled={resending}
              className="mt-3 btn-secondary w-full flex items-center justify-center gap-2 text-sm"
              data-testid="resend-status-email-btn"
            >
              <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
              {resending ? t('adminOrders.resending') : t('adminOrders.resendEmail')}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        warning={confirm?.warning}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm?.type === 'cancel') doAction(() => cancelOrder(id), 'Order cancelled');
          else if (confirm?.type === 'delete') doAction(async () => { await deleteOrder(id); navigate('/admin/orders'); }, 'Order deleted');
          else if (confirm?.type === 'processing') doAction(() => updateOrderStatus(id, { status: 'processing' }).then(res => {
            if (res.data.email_sent) toast.success('Customer notified');
          }), 'Order marked as processing');
          else if (confirm?.type === 'completed') doAction(() => updateOrderStatus(id, { status: 'completed' }).then(res => {
            if (res.data.email_sent) toast.success('Customer notified');
          }), 'Order marked as completed');
          else if (confirm?.type === 'proforma') {
            setSendingProforma(true);
            setConfirm(null);
            sendProforma(id)
              .then(() => { toast.success(t('adminOrders.proformaSentSuccess')); fetchOrder(); })
              .catch(err => toast.error(err.response?.data?.detail || t('adminOrders.failedSendProforma')))
              .finally(() => setSendingProforma(false));
          }
        }}
      />
    </div>
  );
};
